// src/components/AddPositionForm.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Paper,
  Grid,
  Alert,
  Switch,
  FormControlLabel,
  MenuItem,
  Divider,
  Checkbox,
  Chip,
  Tooltip,
  IconButton,
  Collapse,
} from "@mui/material";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useAuthContext } from "../context/AuthContext";
import { ACTIONS, DEFAULT_PERMISSION_CATALOG, mergePermissionCatalogWithDefaults } from "../hooks/usePositions";
import { useWorkspaceSegment } from "../hooks/useWorkspaceSegment";
import { adaptPermissionCatalogForSegment } from "../utils/workspaceSegmentLabels";
import WorkforceHrSection, { hrOutroOverrideMissing } from "./WorkforceHrSection";

// ⚠️ Importa permissões efetivas do usuário logado
import { useEffectivePermissions, toSet, can, PROFILE_OPEN_BY_DEFAULT } from "../hooks/useEffectivePermissions";
import { useChannels } from "../hooks/useChannels";
import { isComposerOutboundChannel } from "../constants/channelCatalog";
import { parseBlockedOutboundChannelIds } from "../utils/cargoChannelSendBlocks";
import {
  CHAT_VISIBILITY_LIMITS,
  applyChatVisibilityLimits,
  canEditChatVisibilityLimit,
  isChatVisibilityLimitAction,
  isChatVisibilityLimitScope,
} from "../utils/chatVisibilityLimits";

const digitsOnly = (s) => String(s ?? "").replace(/[^\d]/g, "");
const n = (v) => (v === "" || v == null ? null : Number(v));

const ACTION_ORDER = [
  "view",
  "create",
  "update",
  "delete",
  "bulk",
  "send",
  "export",
  "block_others",
  "block_queue",
  "block_bots",
  "manage_channels",
  "manage",
  "request_repasse",
  "change_position",
  "adjust",
  "update_identity",
  "connect_google_calendar",
  "connect_personal_whatsapp",
  "update_preferences",
  "manage_notifications",
  "reset_password",
];

/** Nome curto (chips / grade). */
const LABELS = {
  view: "Ver",
  create: "Criar",
  update: "Editar",
  delete: "Excluir",
  export: "Exportar",
  send: "Disparar",
  bulk: "Em massa",
  manage: "Gerir",
  manage_channels: "Canais",
  block_others: "Bloquear outros",
  block_queue: "Bloquear fila",
  block_bots: "Bloquear bots/IA",
  request_repasse: "Repasse",
  change_position: "Definir / alterar cargo",
  adjust: "Ajustar",
  update_identity: "Identidade",
  connect_google_calendar: "Google Agenda",
  connect_personal_whatsapp: "WhatsApp pessoal",
  update_preferences: "Preferências",
  manage_notifications: "Alertas",
  reset_password: "Reset senha",
};

/** Copy orientada a efeito — o que acontece se marcar. */
const ACTION_HELP = {
  view: "Aparece no menu e permite abrir esta tela.",
  create: "Pode cadastrar novos itens.",
  update: "Pode alterar itens já existentes.",
  delete: "Pode excluir itens.",
  export: "Pode baixar ou exportar dados.",
  send: "Pode iniciar ou retomar disparos de campanhas.",
  bulk: "Pode aplicar a mesma ação em vários itens de uma vez.",
  manage: "Pode incluir, editar, excluir, importar, exportar e agir em massa neste recurso.",
  manage_channels: "Pode gerenciar canais do chat interno da equipe.",
  block_others: "Esconde solicitações de outros atendentes — vê só as suas.",
  block_queue: "Esconde a fila sem responsável.",
  block_bots: "Esconde conversas de ChatBots e Agentes de IA.",
  request_repasse: "Pode solicitar repasse de atendimento.",
  change_position:
    "Na criação, edição e exclusão de usuários: controla quem pode ser gerido. Sem cargos marcados, pode usar qualquer um. Se marcar, só cadastra/edita/exclui quem está nesses cargos (e só atribui esses).",
  adjust: "Pode aprovar ou rejeitar justificativas e correções de ponto.",
  update_identity: "Pode alterar nome, e-mail, celular, foto e opção de incluir nome no WhatsApp.",
  connect_google_calendar: "Pode conectar, sincronizar e desconectar a Google Agenda no Meu Perfil.",
  connect_personal_whatsapp: "Pode conectar e desconectar o WhatsApp pessoal (QR) no Meu Perfil.",
  update_preferences: "Pode alterar idioma, tema e modo de receber ligações.",
  manage_notifications: "Pode configurar alertas do painel e Web Push.",
  reset_password: "Pode solicitar e-mail para redefinir a própria senha.",
};

const RESTRICT_ACTIONS = CHAT_VISIBILITY_LIMITS.reduce((set, item) => {
  set.add(item.action);
  return set;
}, new Set());

const ACTION_GROUPS = [
  {
    id: "access",
    title: "Acesso",
    actions: ["view"],
  },
  {
    id: "edit",
    title: "Alterações",
    actions: ["create", "update", "delete", "bulk", "change_position", "update_identity", "update_preferences"],
  },
  {
    id: "extra",
    title: "Extras",
    actions: [
      "send",
      "export",
      "manage_channels",
      "manage",
      "request_repasse",
      "adjust",
      "connect_google_calendar",
      "connect_personal_whatsapp",
      "manage_notifications",
      "reset_password",
    ],
  },
];

/** Pré-marca ações liberadas por padrão quando o cargo ainda não tem nenhum profile.*. */
function applyProfileOpenDefaults(knownScopes = []) {
  const list = Array.isArray(knownScopes) ? [...knownScopes] : [];
  if (list.some((s) => String(s).startsWith("profile."))) return list;
  for (const act of PROFILE_OPEN_BY_DEFAULT) {
    list.push(`profile.${act}`);
  }
  return list;
}

const MODULE_HELP = {
  Menu: "Telas do dia a dia: atendimento, clientes, vendas e afins.",
  Configurações: "Cadastros e ajustes internos do sistema.",
};

const RESTRICT_BG = "#FFF8E1";

function sortActions(actions = []) {
  const rank = new Map(ACTION_ORDER.map((a, i) => [a, i]));
  return [...actions].sort(
    (a, b) => (rank.get(a) ?? 999) - (rank.get(b) ?? 999) || String(a).localeCompare(String(b))
  );
}

function groupActionsForResource(actions = []) {
  const set = new Set(actions);
  return ACTION_GROUPS.map((g) => ({
    ...g,
    actions: g.actions.filter((a) => set.has(a)),
  })).filter((g) => g.actions.length > 0);
}

/** Lê hints de roteamento/recepcionista vindos da API (`routingHints` ou JSON legado). */
function parseRoutingHintsFromPosition(row) {
  const raw = row?.routingHints ?? row?.routing_hints_json;
  if (raw == null || raw === "") {
    return { routingAliases: "", routingNeeds: "", routingNotes: "" };
  }
  let o = raw;
  if (typeof raw === "string") {
    try {
      o = JSON.parse(raw);
    } catch {
      return { routingAliases: "", routingNeeds: "", routingNotes: "" };
    }
  }
  if (!o || typeof o !== "object") {
    return { routingAliases: "", routingNeeds: "", routingNotes: "" };
  }
  return {
    routingAliases: o.customerRoleAliases != null ? String(o.customerRoleAliases) : "",
    routingNeeds: o.needKeywords != null ? String(o.needKeywords) : "",
    routingNotes: o.receptionistNotes != null ? String(o.receptionistNotes) : "",
  };
}

function positionHasRoutingHints(row) {
  const h = parseRoutingHintsFromPosition(row);
  return Boolean(h.routingAliases.trim() || h.routingNeeds.trim() || h.routingNotes.trim());
}

function positionHasHrData(row) {
  if (!row) return false;
  return Boolean(
    row.modalidade_contratacao ||
      (row.salario_mensal != null && row.salario_mensal !== "") ||
      (row.encargos_pct != null && row.encargos_pct !== "") ||
      (row.beneficios_mensal != null && row.beneficios_mensal !== "") ||
      row.jornada_tipo ||
      row.jornada_json
  );
}

function hrSummaryLabel(hr) {
  if (!hr) return "Opcional · para análise de custos";
  const parts = [];
  if (hr.modalidade_contratacao) parts.push(hr.modalidade_contratacao);
  if (hr.salario_mensal != null && hr.salario_mensal !== "") {
    parts.push(
      `R$ ${Number(hr.salario_mensal).toLocaleString("pt-BR", { minimumFractionDigits: 0 })}/mês`
    );
  }
  if (hr.jornada_tipo) parts.push(hr.jornada_tipo);
  if (!parts.length) return "Opcional · para análise de custos";
  return parts.join(" · ");
}

/** Seção opcional expansível — cabeçalho discreto + Collapse. */
function OptionalSection({ title, summary, open, onToggle, children, badge }) {
  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        mb: 1.5,
        overflow: "hidden",
        bgcolor: open ? "background.paper" : "transparent",
      }}
    >
      <Box
        component="button"
        type="button"
        onClick={onToggle}
        sx={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 1.5,
          py: 1.25,
          border: 0,
          bgcolor: "transparent",
          cursor: "pointer",
          textAlign: "left",
          font: "inherit",
          color: "inherit",
          "&:hover": { bgcolor: "action.hover" },
        }}
      >
        <ExpandMoreRoundedIcon
          fontSize="small"
          sx={{
            color: "text.secondary",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s",
          }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={700} component="span">
            {title}
          </Typography>
          {!open && summary ? (
            <Typography
              variant="caption"
              color="text.secondary"
              component="span"
              sx={{ display: "block", mt: 0.15, lineHeight: 1.35 }}
            >
              {summary}
            </Typography>
          ) : null}
        </Box>
        {badge != null ? (
          <Chip size="small" label={badge} variant="outlined" sx={{ height: 22, fontSize: 11 }} />
        ) : (
          <Typography variant="caption" color="text.disabled" sx={{ whiteSpace: "nowrap" }}>
            Opcional
          </Typography>
        )}
      </Box>
      <Collapse in={open}>
        <Box sx={{ px: 2, pb: 2, pt: 0.5 }}>{children}</Box>
      </Collapse>
    </Box>
  );
}

/** ==== Helpers para preservar escopos desconhecidos (fora do catálogo) ==== */
const splitKnownUnknownScopes = (scopes = [], catalog) => {
  const knownKeys = new Set((catalog || []).flatMap((m) => (m.resources || []).map((r) => r.key)));
  const known = [];
  const unknown = [];
  for (const raw of scopes) {
    const [res, act] = String(raw).split(".");
    if (!res || !act) continue;
    (knownKeys.has(res) ? known : unknown).push(`${res}.${act}`);
  }
  return { known, unknown };
};

const mergeScopes = (knownSet, unknownArr) => {
  const out = new Set([...(unknownArr || [])]);
  for (const k of Array.from(knownSet || [])) out.add(k);
  return Array.from(out);
};

function parseAssignablePositionIds(raw) {
  if (!Array.isArray(raw)) return [];
  return [...new Set(raw.map((x) => Number(x)).filter((n) => Number.isFinite(n) && n > 0))];
}

function channelPickerLabel(channel) {
  const name = channel?.nome || channel?.name || `#${channel?.id}`;
  const type = String(channel?.channelType || "").trim();
  if (type && !String(name).toLowerCase().includes(type.toLowerCase())) {
    return `${name} · ${type}`;
  }
  return name;
}

function ChatVisibilityLimitsCard({
  scopesSet,
  allowedSet,
  immutableSet,
  onToggle,
}) {
  return (
    <Box
      sx={{
        mt: 2,
        mb: 2,
        p: 1.5,
        borderRadius: 1,
        border: "1px solid",
        borderColor: "warning.light",
        bgcolor: RESTRICT_BG,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontWeight: 700,
          letterSpacing: 0.3,
          textTransform: "uppercase",
          color: "warning.dark",
          display: "block",
          mb: 0.25,
        }}
      >
        Restringir o que pode ver
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1, lineHeight: 1.35 }}>
        O padrão é ver tudo no atendimento. Marque só o que este cargo não pode ver. Quem já está
        limitado em um ponto não consegue liberar esse ponto em outro cargo.
      </Typography>
      {CHAT_VISIBILITY_LIMITS.map((item) => {
        const allowed = allowedSet.has(item.scope);
        const locked = !allowed && immutableSet.has(item.scope);
        const checked = scopesSet.has(item.scope) || immutableSet.has(item.scope);
        const tip = locked
          ? "Você também está limitado neste ponto, então não pode liberar isso em outro cargo."
          : item.help;
        return (
          <Box
            key={item.scope}
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 2,
              py: 0.75,
            }}
          >
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                  {item.label}
                </Typography>
                {locked && (
                  <Tooltip title={tip}>
                    <LockOutlinedIcon sx={{ fontSize: 14, color: "text.disabled" }} />
                  </Tooltip>
                )}
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.35 }}>
                {item.help}
              </Typography>
            </Box>
            <Tooltip title={locked ? tip : ""} disableHoverListener={!locked}>
              <span>
                <Switch
                  size="small"
                  disabled={!allowed}
                  checked={!!checked}
                  onChange={(e) => onToggle("chat", item.action, e.target.checked)}
                  color="warning"
                  inputProps={{ "aria-label": item.label }}
                />
              </span>
            </Tooltip>
          </Box>
        );
      })}
    </Box>
  );
}

function BlockedChannelsPicker({
  channels = [],
  value = [],
  onChange,
  disabled = false,
}) {
  const options = channels || [];
  const selected = (value || []).map((id) => {
    const found = options.find((ch) => Number(ch.id) === Number(id));
    return found || { id, nome: `Canal #${id}` };
  });

  return (
    <Box sx={{ mt: 0.5 }}>
      <Autocomplete
        multiple
        disableCloseOnSelect
        disabled={disabled}
        options={options}
        value={selected}
        onChange={(_, next) => onChange((next || []).map((ch) => Number(ch.id)).filter(Boolean))}
        getOptionLabel={(ch) => channelPickerLabel(ch)}
        isOptionEqualToValue={(a, b) => String(a.id) === String(b.id)}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={option.id}
              size="small"
              color="warning"
              variant="outlined"
              label={channelPickerLabel(option)}
            />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            label="Canais bloqueados para envio"
            placeholder={selected.length ? "" : "Nenhum = pode enviar por todos"}
            helperText={
              selected.length
                ? "Este cargo não poderá enviar mensagens pelos canais marcados. Os demais continuam liberados."
                : "Por padrão o cargo envia por todos os canais criados. Marque só os que deseja bloquear."
            }
          />
        )}
      />
    </Box>
  );
}

function AssignablePositionsPicker({
  allPositions = [],
  value = [],
  onChange,
  disabled = false,
}) {
  const options = allPositions || [];
  const selected = options.filter((p) => value.includes(Number(p.id)));

  return (
    <Box sx={{ mt: 1, mb: 0.5, pl: 1 }}>
      <Autocomplete
        multiple
        disableCloseOnSelect
        disabled={disabled}
        options={options}
        value={selected}
        onChange={(_, next) => onChange((next || []).map((p) => Number(p.id)).filter(Boolean))}
        getOptionLabel={(p) => p?.nome || `#${p?.id}`}
        isOptionEqualToValue={(a, b) => String(a.id) === String(b.id)}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => (
            <Chip {...getTagProps({ index })} key={option.id} size="small" label={option.nome} />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            label="Cargos permitidos (criar, editar e excluir)"
            placeholder={selected.length ? "" : "Nenhum = qualquer cargo"}
            helperText={
              selected.length
                ? "Vale na criação, edição e exclusão: só esses cargos. Só é possível gerir usuários que já estão em um deles."
                : "Nenhum marcado: na criação, edição e exclusão pode gerir qualquer cargo. Marque alguns para limitar."
            }
          />
        )}
      />
    </Box>
  );
}

/* ================= Editor de permissões (guided + grade) ================ */
const COL_W = 72;
const RES_W = 220;
const RESTRICT_BG_ZEBRA = "#FFF3E0";

const PermissionMatrix = ({
  catalog = DEFAULT_PERMISSION_CATALOG,
  scopesSet,
  onToggle,
  allowedSet,
  immutableSet,
  allPositions = [],
  assignablePositionIds = [],
  onAssignablePositionIdsChange = () => {},
}) => {
  const actsFor = (r) => (Array.isArray(r?.actions) && r.actions.length ? r.actions : ACTIONS);
  const has = (resKey, act) => scopesSet.has(`${resKey}.${act}`);

  const isVisible = (resKey, act) => {
    if (isChatVisibilityLimitAction(act)) return false;
    const key = `${resKey}.${act}`;
    return allowedSet.has(key) || immutableSet.has(key);
  };
  const isLocked = (resKey, act) => {
    const key = `${resKey}.${act}`;
    return !allowedSet.has(key) && immutableSet.has(key);
  };

  // Módulos fechados por padrão — progressive disclosure
  const [expandedModules, setExpandedModules] = useState(() => new Set());
  const [expandedResources, setExpandedResources] = useState(() => new Set());
  const [search, setSearch] = useState("");
  const [onlySelected, setOnlySelected] = useState(false);
  const [viewMode, setViewMode] = useState("guided"); // guided | grid

  const toggleModule = (moduleName) =>
    setExpandedModules((prev) => {
      const next = new Set(prev);
      next.has(moduleName) ? next.delete(moduleName) : next.add(moduleName);
      return next;
    });

  const toggleResource = (resKey) =>
    setExpandedResources((prev) => {
      const next = new Set(prev);
      next.has(resKey) ? next.delete(resKey) : next.add(resKey);
      return next;
    });

  const collapseAll = () => {
    setExpandedModules(new Set());
    setExpandedResources(new Set());
  };

  const visibleActsFor = (r) => sortActions(actsFor(r).filter((a) => isVisible(r.key, a)));
  const allowedActsFor = (r) =>
    actsFor(r).filter((a) => !isChatVisibilityLimitAction(a) && allowedSet.has(`${r.key}.${a}`));

  const allActs = (r) => {
    const visibles = visibleActsFor(r);
    return visibles.length > 0 && visibles.every((a) => has(r.key, a));
  };

  const someActs = (r) => {
    const visibles = visibleActsFor(r);
    const checked = visibles.filter((a) => has(r.key, a)).length;
    return checked > 0 && checked < visibles.length;
  };

  const toggleResourceAll = (r) => {
    const allowed = allowedActsFor(r);
    if (allowed.length === 0) return;
    const enable = !allowed.every((a) => has(r.key, a));
    allowed.forEach((a) => onToggle(r.key, a, enable));
  };

  const applyResourcePreset = (r, kind) => {
    const allowed = allowedActsFor(r);
    if (allowed.length === 0) return;
    if (kind === "clear") {
      allowed.forEach((a) => onToggle(r.key, a, false));
      return;
    }
    if (kind === "view") {
      allowed.forEach((a) => onToggle(r.key, a, a === "view"));
      return;
    }
    if (kind === "full") {
      // Libera tudo que não é restrição
      allowed.forEach((a) => onToggle(r.key, a, !RESTRICT_ACTIONS.has(a)));
    }
  };

  const columnState = (mod, action) => {
    const eligible = (mod.resources || []).filter((r) => Array.isArray(r.actions) && r.actions.includes(action));
    const total = eligible.length;
    const checked = eligible.filter((r) => has(r.key, action)).length;
    return { total, checked, all: total > 0 && checked === total, some: total > 0 && checked > 0 && checked < total };
  };

  const toggleColumn = (mod, action) => {
    const eligible = (mod.resources || []).filter((r) => Array.isArray(r.actions) && r.actions.includes(action));
    const allowed = eligible.filter((r) => allowedSet.has(`${r.key}.${action}`));
    if (allowed.length === 0) return;
    const allAllowedChecked = allowed.every((r) => has(r.key, action));
    allowed.forEach((r) => onToggle(r.key, action, !allAllowedChecked));
  };

  const moduleCounts = (mod) => {
    let total = 0;
    let selected = 0;
    let resourcesWithAny = 0;
    (mod.resources || []).forEach((r) => {
      const acts = Array.isArray(r.actions) ? r.actions : [];
      total += acts.length;
      let any = false;
      acts.forEach((a) => {
        if (has(r.key, a)) {
          selected++;
          any = true;
        }
      });
      if (any) resourcesWithAny++;
    });
    return { total, selected, resourcesWithAny, resourceCount: (mod.resources || []).length };
  };

  const filterCatalog = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (catalog || [])
      .map((mod) => {
        const visibleActionSet = new Set();
        const filteredResources = (mod.resources || [])
          .map((r) => {
            const visActs = visibleActsFor(r);
            if (visActs.length === 0) return null;

            const matchesQ = !q || r.label.toLowerCase().includes(q) || r.key.toLowerCase().includes(q);
            const selectedAny = visActs.some((a) => has(r.key, a));
            if (!matchesQ || (onlySelected && !selectedAny)) return null;

            visActs.forEach((a) => visibleActionSet.add(a));
            return { ...r, actions: visActs };
          })
          .filter(Boolean);

        return {
          ...mod,
          resources: filteredResources,
          visibleActions: sortActions(Array.from(visibleActionSet)),
        };
      })
      .filter((m) => (m.resources || []).length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalog, search, onlySelected, scopesSet, allowedSet, immutableSet]);

  // Ao buscar, abre os módulos com resultado
  useEffect(() => {
    const q = search.trim().toLowerCase();
    if (!q) return;
    setExpandedModules((prev) => {
      const next = new Set(prev);
      filterCatalog.forEach((m) => next.add(m.module));
      return next;
    });
    if (filterCatalog.length === 1 && filterCatalog[0].resources?.length === 1) {
      setExpandedResources(new Set([filterCatalog[0].resources[0].key]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const stickyRes = {
    position: "sticky",
    left: 0,
    zIndex: 2,
    backgroundColor: "background.paper",
    minWidth: RES_W,
    maxWidth: RES_W,
    width: RES_W,
  };

  const renderPermissionSwitch = (r, a) => {
    const key = `${r.key}.${a}`;
    const allowed = allowedSet.has(key);
    const locked = isLocked(r.key, a);
    const checked = has(r.key, a);
    const isRestrict = RESTRICT_ACTIONS.has(a);
    const tip = locked
      ? isRestrict
        ? "Você também está limitado neste ponto, então não pode liberar isso em outro cargo."
        : "Já está neste cargo, mas você não tem esta permissão para alterá-la."
      : ACTION_HELP[a] || LABELS[a] || a;

    const showAssignablePicker = r.key === "users" && a === "change_position" && checked;

    return (
      <Box key={a}>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 2,
            py: 0.75,
            px: 1,
            borderRadius: 1,
            bgcolor: isRestrict ? RESTRICT_BG : "transparent",
          }}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                {LABELS[a] ?? a}
              </Typography>
              {locked && (
                <Tooltip title={tip}>
                  <LockOutlinedIcon sx={{ fontSize: 14, color: "text.disabled" }} />
                </Tooltip>
              )}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.35 }}>
              {ACTION_HELP[a] || ""}
            </Typography>
          </Box>
          <Tooltip title={locked ? tip : ""} disableHoverListener={!locked}>
            <span>
              <Switch
                size="small"
                disabled={!allowed}
                checked={!!checked}
                onChange={(e) => onToggle(r.key, a, e.target.checked)}
                color={isRestrict ? "warning" : "primary"}
                inputProps={{ "aria-label": `${r.label}: ${LABELS[a] ?? a}` }}
              />
            </span>
          </Tooltip>
        </Box>
        {showAssignablePicker && (
          <AssignablePositionsPicker
            allPositions={allPositions}
            value={assignablePositionIds}
            onChange={onAssignablePositionIdsChange}
            disabled={!allowed}
          />
        )}
      </Box>
    );
  };

  const renderGuidedResource = (r) => {
    const acts = visibleActsFor(r);
    const active = acts.filter((a) => has(r.key, a));
    const isOpen = expandedResources.has(r.key) || acts.length <= 1;
    const canExpand = acts.length > 1;
    const groups = groupActionsForResource(acts);
    const onlyView = acts.length === 1 && acts[0] === "view";

    return (
      <Box
        key={r.key}
        sx={{
          borderBottom: "1px solid",
          borderColor: "divider",
          "&:last-child": { borderBottom: 0 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 1.5,
            py: 1,
            "&:hover": { bgcolor: "action.hover" },
          }}
        >
          <Checkbox
            size="small"
            checked={allActs(r)}
            indeterminate={someActs(r)}
            onChange={() => toggleResourceAll(r)}
            sx={{ p: 0.5 }}
            inputProps={{ "aria-label": `Alternar todas de ${r.label}` }}
          />

          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              cursor: canExpand ? "pointer" : "default",
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
            }}
            onClick={() => canExpand && toggleResource(r.key)}
            role={canExpand ? "button" : undefined}
            tabIndex={canExpand ? 0 : undefined}
            onKeyDown={(e) => {
              if (canExpand && (e.key === "Enter" || e.key === " ")) {
                e.preventDefault();
                toggleResource(r.key);
              }
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {r.label}
            </Typography>

            {active.length === 0 ? (
              <Typography variant="caption" color="text.secondary">
                Sem permissões
              </Typography>
            ) : (
              <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                {active.slice(0, 4).map((a) => (
                  <Chip
                    key={a}
                    size="small"
                    label={LABELS[a] ?? a}
                    variant="outlined"
                    sx={{
                      height: 22,
                      fontSize: 11,
                      ...(RESTRICT_ACTIONS.has(a)
                        ? { bgcolor: RESTRICT_BG, borderColor: "warning.light", color: "warning.dark" }
                        : null),
                    }}
                  />
                ))}
                {active.length > 4 && (
                  <Chip size="small" label={`+${active.length - 4}`} sx={{ height: 22, fontSize: 11 }} />
                )}
              </Box>
            )}
          </Box>

          {canExpand && (
            <IconButton
              size="small"
              onClick={() => toggleResource(r.key)}
              aria-label={isOpen ? "Recolher detalhes" : "Ver permissões"}
              sx={{
                transform: isOpen ? "rotate(180deg)" : "none",
                transition: "transform 0.15s",
              }}
            >
              <ExpandMoreRoundedIcon fontSize="small" />
            </IconButton>
          )}
        </Box>

        {/* Recurso só com "Ver": o checkbox da linha já basta; switch inline opcional omitido */}
        {onlyView ? null : (
          <Collapse in={!!isOpen} unmountOnExit>
            <Box sx={{ px: 2, pb: 1.5, pt: 0.25, bgcolor: "grey.50" }}>
              <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mb: 1 }}>
                <Button size="small" variant="text" onClick={() => applyResourcePreset(r, "view")} sx={{ textTransform: "none" }}>
                  Só ver
                </Button>
                <Button size="small" variant="text" onClick={() => applyResourcePreset(r, "full")} sx={{ textTransform: "none" }}>
                  Liberar tudo
                </Button>
                <Button size="small" variant="text" color="inherit" onClick={() => applyResourcePreset(r, "clear")} sx={{ textTransform: "none" }}>
                  Limpar
                </Button>
              </Box>

              {groups.map((g) => (
                <Box
                  key={g.id}
                  sx={{
                    mb: 1.25,
                    ...(g.restrict
                      ? {
                          p: 1,
                          borderRadius: 1,
                          border: "1px solid",
                          borderColor: "warning.light",
                          bgcolor: RESTRICT_BG,
                        }
                      : null),
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      letterSpacing: 0.3,
                      textTransform: "uppercase",
                      color: g.restrict ? "warning.dark" : "text.secondary",
                      display: "block",
                      mb: 0.25,
                      px: g.restrict ? 0 : 1,
                    }}
                  >
                    {g.title}
                  </Typography>
                  {g.hint && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75, lineHeight: 1.35 }}>
                      {g.hint}
                    </Typography>
                  )}
                  {g.actions.map((a) => renderPermissionSwitch(r, a))}
                </Box>
              ))}
            </Box>
          </Collapse>
        )}
      </Box>
    );
  };

  const renderGrid = (mod) => {
    const visibleActions = mod.visibleActions || [];
    return (
      <Box sx={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
        <Box
          role="table"
          sx={{
            display: "grid",
            gridTemplateColumns: `${RES_W}px repeat(${Math.max(visibleActions.length, 1)}, ${COL_W}px)`,
            minWidth: RES_W + visibleActions.length * COL_W,
          }}
        >
          <Box
            role="columnheader"
            sx={{
              ...stickyRes,
              zIndex: 3,
              px: 1.5,
              py: 1,
              borderBottom: "1px solid",
              borderColor: "divider",
              display: "flex",
              alignItems: "flex-end",
              bgcolor: "grey.50",
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              Recurso
            </Typography>
          </Box>

          {visibleActions.map((a) => {
            const cs = columnState(mod, a);
            const isRestrict = RESTRICT_ACTIONS.has(a);
            return (
              <Tooltip key={`h-${a}`} title={ACTION_HELP[a] || LABELS[a] || a} arrow placement="top">
                <Box
                  role="columnheader"
                  sx={{
                    px: 0.5,
                    py: 0.75,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    bgcolor: isRestrict ? RESTRICT_BG : "grey.50",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    borderLeft: isRestrict && a === "block_others" ? "2px solid" : undefined,
                    borderLeftColor: "warning.main",
                  }}
                >
                  <Checkbox
                    size="small"
                    disabled={cs.total === 0}
                    checked={cs.all}
                    indeterminate={cs.some}
                    onChange={() => toggleColumn(mod, a)}
                    sx={{ p: 0.25 }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      fontSize: 10,
                      lineHeight: 1.15,
                      textAlign: "center",
                      color: isRestrict ? "warning.dark" : "text.secondary",
                    }}
                  >
                    {LABELS[a] ?? a}
                  </Typography>
                </Box>
              </Tooltip>
            );
          })}

          {(mod.resources || []).map((r, rowIdx) => {
            const zebra = rowIdx % 2 === 1;
            const rowBg = zebra ? "action.hover" : "background.paper";
            return (
              <React.Fragment key={r.key}>
                <Box
                  role="rowheader"
                  sx={{
                    ...stickyRes,
                    backgroundColor: rowBg,
                    px: 1,
                    py: 0.25,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    boxShadow: "4px 0 8px -6px rgba(0,0,0,0.18)",
                  }}
                >
                  <Checkbox size="small" checked={allActs(r)} indeterminate={someActs(r)} onChange={() => toggleResourceAll(r)} sx={{ p: 0.5 }} />
                  <Typography variant="body2" sx={{ fontSize: 13, lineHeight: 1.25 }} noWrap>
                    {r.label}
                  </Typography>
                </Box>
                {visibleActions.map((a) => {
                  const actionVisible = Array.isArray(r.actions) && r.actions.includes(a);
                  const isRestrict = RESTRICT_ACTIONS.has(a);
                  if (!actionVisible) {
                    return (
                      <Box
                        key={a}
                        sx={{
                          bgcolor: isRestrict ? (zebra ? RESTRICT_BG_ZEBRA : RESTRICT_BG) : rowBg,
                          borderBottom: "1px solid",
                          borderColor: "divider",
                        }}
                      />
                    );
                  }
                  const allowed = allowedSet.has(`${r.key}.${a}`);
                  const locked = isLocked(r.key, a);
                  return (
                    <Tooltip
                      key={a}
                      title={
                        locked
                          ? isRestrict
                            ? "Você também está limitado neste ponto, então não pode liberar isso em outro cargo."
                            : "Já no cargo; você não pode alterar."
                          : ACTION_HELP[a] || LABELS[a] || a
                      }
                      arrow
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: isRestrict ? (zebra ? RESTRICT_BG_ZEBRA : RESTRICT_BG) : rowBg,
                          borderBottom: "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        <Checkbox
                          size="small"
                          disabled={!allowed}
                          checked={!!has(r.key, a)}
                          onChange={(e) => onToggle(r.key, a, e.target.checked)}
                          sx={{ p: 0.5 }}
                        />
                      </Box>
                    </Tooltip>
                  );
                })}
              </React.Fragment>
            );
          })}
        </Box>
        {mod.resources?.some((r) => r.key === "users") && has("users", "change_position") && (
          <Box sx={{ px: 2, py: 1.5 }}>
            <AssignablePositionsPicker
              allPositions={allPositions}
              value={assignablePositionIds}
              onChange={onAssignablePositionIdsChange}
              disabled={!allowedSet.has("users.change_position")}
            />
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
        <TextField
          size="small"
          placeholder="Buscar tela ou recurso…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 240, flex: "1 1 220px" }}
        />
        <FormControlLabel
          control={<Switch checked={onlySelected} onChange={(e) => setOnlySelected(e.target.checked)} size="small" />}
          label={<Typography variant="body2">Só com permissão</Typography>}
        />
        <Box sx={{ display: "flex", gap: 0.5, ml: "auto" }}>
          <Button
            size="small"
            variant={viewMode === "guided" ? "contained" : "outlined"}
            onClick={() => setViewMode("guided")}
            sx={{ textTransform: "none" }}
          >
            Lista
          </Button>
          <Button
            size="small"
            variant={viewMode === "grid" ? "contained" : "outlined"}
            onClick={() => setViewMode("grid")}
            sx={{ textTransform: "none" }}
          >
            Grade
          </Button>
          <Button size="small" onClick={collapseAll} sx={{ textTransform: "none" }}>
            Recolher
          </Button>
        </Box>
      </Box>

      {viewMode === "guided" && (
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.45 }}>
          Abra um grupo, depois uma tela. O checkbox da linha liga/desliga tudo dali; use o detalhe para ajustar
          e entender o efeito de cada permissão.
        </Typography>
      )}

      {filterCatalog.map((mod) => {
        const isOpen = expandedModules.has(mod.module);
        const { selected, resourcesWithAny, resourceCount } = moduleCounts(mod);

        return (
          <Paper key={mod.module} variant="outlined" sx={{ overflow: "hidden" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 1.5,
                py: 1.25,
                bgcolor: isOpen ? "grey.50" : "background.paper",
                cursor: "pointer",
                userSelect: "none",
              }}
              onClick={() => toggleModule(mod.module)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  toggleModule(mod.module);
                }
              }}
            >
              <ExpandMoreRoundedIcon
                fontSize="small"
                sx={{
                  transform: isOpen ? "rotate(180deg)" : "none",
                  transition: "transform 0.15s",
                  color: "text.secondary",
                }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {mod.module}
                  </Typography>
                  <Chip
                    size="small"
                    variant="outlined"
                    label={
                      resourcesWithAny > 0
                        ? `${resourcesWithAny} de ${resourceCount} telas`
                        : `${resourceCount} telas`
                    }
                  />
                  {selected > 0 && (
                    <Typography variant="caption" color="text.secondary">
                      {selected} permissões ativas
                    </Typography>
                  )}
                </Box>
                {!isOpen && MODULE_HELP[mod.module] && (
                  <Typography variant="caption" color="text.secondary">
                    {MODULE_HELP[mod.module]}
                  </Typography>
                )}
              </Box>
            </Box>

            <Collapse in={isOpen} unmountOnExit>
              <Divider />
              {viewMode === "guided" ? (
                <Box>{(mod.resources || []).map((r) => renderGuidedResource(r))}</Box>
              ) : (
                renderGrid(mod)
              )}
            </Collapse>
          </Paper>
        );
      })}

      {filterCatalog.length === 0 && (
        <Alert severity="info">Nenhum recurso encontrado com esses filtros.</Alert>
      )}
    </Box>
  );
};

/* =========================== Formulário =========================== */
const AddPositionForm = ({ data, catalog: catalogProp, allPositions = [], onClose, onSubmit, isLoading }) => {
  const { userData } = useAuthContext();
  const isMasterUser = Number(userData?.master) === 1;
  const { segment } = useWorkspaceSegment();

  /** Catálogo filtrado/renomeado pelo segmento (Trechos, BPe/passagens só em rodoviário, etc.). */
  const catalog = useMemo(
    () =>
      adaptPermissionCatalogForSegment(
        mergePermissionCatalogWithDefaults(
          Array.isArray(catalogProp) && catalogProp.length ? catalogProp : DEFAULT_PERMISSION_CATALOG
        ),
        segment
      ),
    [catalogProp, segment]
  );

  // permissões efetivas do usuário logado
  const { data: permData } = useEffectivePermissions();
  const userScopeSet = useMemo(() => toSet(permData?.scopes || []), [permData?.scopes]);

  const [errorMessage, setErrorMessage] = useState(null);
  const [showRouting, setShowRouting] = useState(() => positionHasRoutingHints(data));
  const [showHr, setShowHr] = useState(() => positionHasHrData(data));
  const [showDescription, setShowDescription] = useState(() => Boolean(String(data?.descricao || "").trim()));
  const isCreate = !data?.id;

  // Inicialização levando em conta catálogo (preserva desconhecidos)
  const initialScopesRaw = useMemo(() => {
    return Array.isArray(data?.permissions) ? data.permissions : (Array.isArray(data?.scopes) ? data.scopes : []);
  }, [data?.id]);

  const { known: initialKnown, unknown: initialUnknown } = useMemo(
    () => {
      const split = splitKnownUnknownScopes(initialScopesRaw, catalog);
      return {
        known: applyProfileOpenDefaults(split.known),
        unknown: split.unknown,
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data?.id, JSON.stringify(catalog)]
  );

  // Set(s) essenciais
  const initialKnownSet = useMemo(() => new Set(initialKnown), [initialKnown]);

  const routingInit = parseRoutingHintsFromPosition(data);
  const hrInit = {
    modalidade_contratacao: data?.modalidade_contratacao || "",
    salario_mensal: data?.salario_mensal ?? "",
    encargos_pct: data?.encargos_pct ?? "",
    beneficios_mensal: data?.beneficios_mensal ?? "",
    jornada_tipo: data?.jornada_tipo || "",
    jornada_json: data?.jornada_json || null,
    funcao_confianca: data?.funcao_confianca ?? null,
    regime_jornada_override: data?.regime_jornada_override || "",
    estagio_limite_diario_min: data?.estagio_limite_diario_min ?? "",
    estagio_limite_semanal_min: data?.estagio_limite_semanal_min ?? "",
  };
  const [form, setForm] = useState({
    ...data,
    nome: data?.nome || "",
    descricao: data?.descricao || "",
    ordem: data?.ordem ?? "",
    status: data?.status == null ? 1 : Number(data.status),
    id_usuario: data?.id_usuario || userData?.id,
    id_usuario_ultima_alteracao: userData?.id,
    routingAliases: routingInit.routingAliases,
    routingNeeds: routingInit.routingNeeds,
    routingNotes: routingInit.routingNotes,
    ...hrInit,
  });
  const [hr, setHr] = useState(hrInit);

  const [scopesSet, setScopesSet] = useState(new Set(initialKnown));
  const [unknownScopes, setUnknownScopes] = useState(initialUnknown);
  const [cloneFromId, setCloneFromId] = useState("");
  const [assignablePositionIds, setAssignablePositionIds] = useState(() =>
    parseAssignablePositionIds(data?.assignablePositionIds ?? data?.assignable_position_ids)
  );
  const [blockedOutboundChannelIds, setBlockedOutboundChannelIds] = useState(() =>
    parseBlockedOutboundChannelIds(
      data?.blockedOutboundChannelIds ?? data?.blocked_outbound_channel_ids
    )
  );
  const { data: channelsData } = useChannels();
  const outboundChannels = useMemo(
    () => (channelsData?.channels || []).filter((ch) => isComposerOutboundChannel(ch)),
    [channelsData]
  );

  // Conjunto de escopos que o usuário PODE alterar (tem o mesmo escopo).
  // Limites de visão (chat.block_*) são o inverso: quem sofre o limite não mexe.
  const allowedSet = useMemo(() => {
    const out = new Set();
    const canEditPositions =
      isMasterUser || can(userScopeSet, "positions", "update") || can(userScopeSet, "positions", "create");
    catalog.forEach((mod) =>
      mod.resources.forEach((r) => {
        const acts = Array.isArray(r.actions) && r.actions.length ? r.actions : ACTIONS;
        acts.forEach((a) => {
          const key = `${r.key}.${a}`;
          if (isChatVisibilityLimitAction(a)) {
            if (
              canEditPositions &&
              canEditChatVisibilityLimit(key, { isMaster: isMasterUser, actorScopes: userScopeSet })
            ) {
              out.add(key);
            }
            return;
          }
          if (isMasterUser || can(userScopeSet, r.key, a)) {
            out.add(key);
            return;
          }
          // Ação nova: quem já gerencia usuários/cargos precisa conseguir ligá-la na matriz.
          if (
            r.key === "users" &&
            a === "change_position" &&
            (can(userScopeSet, "users", "update") || can(userScopeSet, "positions", "update"))
          ) {
            out.add(key);
          }
          // Gestor de cargos precisa liberar/restringir Meu Perfil mesmo sem ter o scope em si.
          if (
            r.key === "profile" &&
            (can(userScopeSet, "positions", "update") || can(userScopeSet, "positions", "create"))
          ) {
            out.add(key);
          }
        });
      })
    );
    return out;
  }, [catalog, userScopeSet, isMasterUser]);

  // Escopos IMUTÁVEIS: estão setados no cargo, mas o usuário não tem permissão => aparecem, mas desabilitados
  const immutableSet = useMemo(() => {
    const out = new Set();
    initialKnownSet.forEach((k) => {
      if (!allowedSet.has(k)) out.add(k);
    });
    return out;
  }, [initialKnownSet, allowedSet]);

  useEffect(() => {
    const rh = parseRoutingHintsFromPosition(data);
    setForm((p) => ({
      ...p,
      ...data,
      nome: data?.nome || "",
      descricao: data?.descricao || "",
      ordem: data?.ordem ?? "",
      status: data?.status == null ? 1 : Number(data.status),
      id_usuario: data?.id_usuario || userData?.id,
      id_usuario_ultima_alteracao: userData?.id,
      routingAliases: rh.routingAliases,
      routingNeeds: rh.routingNeeds,
      routingNotes: rh.routingNotes,
    }));

    const raw = Array.isArray(data?.permissions) ? data.permissions : (Array.isArray(data?.scopes) ? data.scopes : []);
    const { known, unknown } = splitKnownUnknownScopes(raw, catalog);
    setScopesSet(new Set(applyProfileOpenDefaults(known)));
    setUnknownScopes(unknown);
    setAssignablePositionIds(parseAssignablePositionIds(data?.assignablePositionIds ?? data?.assignable_position_ids));
    setBlockedOutboundChannelIds(
      parseBlockedOutboundChannelIds(
        data?.blockedOutboundChannelIds ?? data?.blocked_outbound_channel_ids
      )
    );

    setCloneFromId("");
    if (positionHasRoutingHints(data)) setShowRouting(true);
    if (positionHasHrData(data)) setShowHr(true);
    if (String(data?.descricao || "").trim()) setShowDescription(true);

    const nextHr = {
      modalidade_contratacao: data?.modalidade_contratacao || "",
      salario_mensal: data?.salario_mensal ?? "",
      encargos_pct: data?.encargos_pct ?? "",
      beneficios_mensal: data?.beneficios_mensal ?? "",
      jornada_tipo: data?.jornada_tipo || "",
      jornada_json: data?.jornada_json || null,
      funcao_confianca: data?.funcao_confianca ?? null,
      regime_jornada_override: data?.regime_jornada_override || "",
      estagio_limite_diario_min: data?.estagio_limite_diario_min ?? "",
      estagio_limite_semanal_min: data?.estagio_limite_semanal_min ?? "",
    };
    setHr(nextHr);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.id, JSON.stringify(catalog)]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // Bloqueia toggles em escopos não permitidos; preserva imutáveis
  const toggleScope = (resource, action, enabled) => {
    const key = `${resource}.${action}`;
    if (!allowedSet.has(key)) return; // sem acesso → não altera
    setScopesSet((prev) => {
      const next = new Set(prev);
      if (enabled) next.add(key);
      else {
        // não permitir remover se for imutável (mas imutável nunca cai aqui pois !allowed, de qualquer forma fica protegido)
        next.delete(key);
      }
      return next;
    });
    if (resource === "users" && action === "change_position" && !enabled) {
      setAssignablePositionIds([]);
    }
  };

  const cloneFrom = (id) => {
    setCloneFromId(id);
    const src = allPositions.find((p) => String(p.id) === String(id));
    if (!src) return;
    const raw = Array.isArray(src.permissions) ? src.permissions : (Array.isArray(src.scopes) ? src.scopes : []);
    const { known /*, unknown*/ } = splitKnownUnknownScopes(raw, catalog);

    // Aplica apenas o que o usuário pode alterar. Mantém escopos imutáveis originais do cargo sendo editado.
    const next = new Set(scopesSet);
    // zera somente os que pode mexer
    for (const k of Array.from(next)) {
      if (allowedSet.has(k)) next.delete(k);
    }
    // adiciona do clone apenas os allowed
    known.forEach((k) => {
      if (allowedSet.has(k)) next.add(k);
    });
    setScopesSet(next);
    if (allowedSet.has("users.change_position")) {
      setAssignablePositionIds(
        known.includes("users.change_position")
          ? parseAssignablePositionIds(src.assignablePositionIds ?? src.assignable_position_ids)
          : []
      );
    }
    setBlockedOutboundChannelIds(
      parseBlockedOutboundChannelIds(
        src.blockedOutboundChannelIds ?? src.blocked_outbound_channel_ids
      )
    );

    // unknownScopes do cargo atual permanecem
    setUnknownScopes((prev) => prev);
  };

  const buildPayload = () => {
    // Garante que imutáveis sempre estejam no payload
    const combinedEditable = new Set(scopesSet);
    immutableSet.forEach((k) => combinedEditable.add(k));
    const combinedScopes = applyChatVisibilityLimits({
      incomingScopes: mergeScopes(combinedEditable, unknownScopes),
      existingScopes: initialKnown,
      isCreate,
      actorScopes: userScopeSet,
      isMaster: isMasterUser,
    });
    const ra = (form.routingAliases != null ? String(form.routingAliases) : "").trim();
    const rn = (form.routingNeeds != null ? String(form.routingNeeds) : "").trim();
    const ro = (form.routingNotes != null ? String(form.routingNotes) : "").trim();
    const routing_hints =
      ra || rn || ro
        ? { customerRoleAliases: ra, needKeywords: rn, receptionistNotes: ro }
        : null;

    return {
      id: form.id,
      nome: (form.nome || "").trim(),
      descricao: form.descricao || "",
      ordem: n(digitsOnly(form.ordem)),
      status: Number(form.status) === 1 ? 1 : 0,
      id_usuario: form.id_usuario || userData?.id,
      id_usuario_ultima_alteracao: userData?.id,
      permissions: combinedScopes,
      scopes: combinedScopes,
      routing_hints,
      modalidade_contratacao: hr.modalidade_contratacao || null,
      salario_mensal: hr.salario_mensal === "" || hr.salario_mensal == null ? null : Number(hr.salario_mensal),
      encargos_pct: hr.encargos_pct === "" || hr.encargos_pct == null ? null : Number(hr.encargos_pct),
      beneficios_mensal:
        hr.beneficios_mensal === "" || hr.beneficios_mensal == null ? null : Number(hr.beneficios_mensal),
      jornada_tipo: hr.jornada_tipo || null,
      jornada_json: hr.jornada_json || null,
      funcao_confianca:
        hr.funcao_confianca === "" || hr.funcao_confianca == null ? null : Number(hr.funcao_confianca),
      regime_jornada_override: hr.regime_jornada_override || null,
      estagio_limite_diario_min:
        hr.estagio_limite_diario_min === "" || hr.estagio_limite_diario_min == null
          ? null
          : Number(hr.estagio_limite_diario_min),
      estagio_limite_semanal_min:
        hr.estagio_limite_semanal_min === "" || hr.estagio_limite_semanal_min == null
          ? null
          : Number(hr.estagio_limite_semanal_min),
      assignablePositionIds: combinedScopes.includes("users.change_position")
        ? assignablePositionIds
        : [],
      blockedOutboundChannelIds,
    };
  };

  const submit = async (e) => {
    e.preventDefault();
    setErrorMessage(null);
    try {
      const payload = buildPayload();
      if (!payload.nome) throw new Error("Nome é obrigatório.");
      if (hrOutroOverrideMissing(hr)) {
        throw new Error("Modalidade Outro exige escolher se há controle de horário.");
      }
      await onSubmit(payload);
    } catch (err) {
      setErrorMessage(err?.response?.data?.message || err?.message || "Erro ao salvar o cargo.");
    }
  };

  const totalVisibleSelected = useMemo(() => {
    // conta apenas o que é visível (allowed ∪ immutable) dentro do catálogo
    let selected = 0;
    let total = 0;
    catalog.forEach((mod) =>
      mod.resources.forEach((r) => {
        const acts = Array.isArray(r.actions) && r.actions.length ? r.actions : ACTIONS;
        acts.forEach((a) => {
          if (isChatVisibilityLimitAction(a)) return;
          const key = `${r.key}.${a}`;
          const visible = allowedSet.has(key) || immutableSet.has(key);
          if (visible) {
            total++;
            if (scopesSet.has(key) || immutableSet.has(key)) selected++;
          }
        });
      })
    );
    return { selected, total };
  }, [catalog, scopesSet, allowedSet, immutableSet]);

  return (
    <Paper elevation={3} sx={{ padding: 3, width: "95%", margin: "0 auto" }}>
      <Typography variant="h6" gutterBottom>
        {form?.id ? "Editar cargo" : "Novo cargo"}
      </Typography>
      {isCreate ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 560, lineHeight: 1.45 }}>
          Para começar, basta um nome e as permissões. O restante é opcional e pode ser configurado depois.
        </Typography>
      ) : null}

      {errorMessage && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{errorMessage}</Alert>
        </Box>
      )}

      <form onSubmit={submit}>
        <Grid container spacing={2} sx={{ mb: 1 }}>
          <Grid item xs={12} md={9}>
            <TextField
              label="Nome do cargo"
              name="nome"
              value={form.nome}
              onChange={handleChange}
              fullWidth
              required
              autoFocus={isCreate}
              placeholder="Ex.: Atendente, Supervisor, Financeiro"
              helperText="Como o perfil aparece na lista de usuários"
            />
          </Grid>

          <Grid item xs={12} md={3} sx={{ display: "flex", alignItems: "center" }}>
            <FormControlLabel
              control={
                <Switch
                  checked={Number(form.status) === 1}
                  onChange={(e) => setForm((p) => ({ ...p, status: e.target.checked ? 1 : 0 }))}
                />
              }
              label={Number(form.status) === 1 ? "Ativo" : "Inativo"}
            />
          </Grid>
        </Grid>

        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            p: 2,
            mb: 2,
            bgcolor: "action.hover",
          }}
        >
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              O que este cargo pode fazer
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, lineHeight: 1.45 }}>
              Defina o acesso ao sistema. Use um atalho ou copie de outro cargo — depois ajuste só o que precisar.
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap", mb: 1.5 }}>
            <TextField
              select
              size="small"
              label="Copiar de outro cargo"
              value={cloneFromId}
              onChange={(e) => cloneFrom(e.target.value)}
              sx={{ minWidth: 240 }}
            >
              <MenuItem value="">—</MenuItem>
              {allPositions
                .filter((p) => String(p.id) !== String(form.id))
                .map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.nome}
                  </MenuItem>
                ))}
            </TextField>

            <Chip
              size="small"
              label={`${totalVisibleSelected.selected} permissões`}
              color={totalVisibleSelected.selected > 0 ? "primary" : "default"}
              variant="outlined"
            />

            <Tooltip title="Copiar lista de permissões (texto)">
              <IconButton
                size="small"
                onClick={() => {
                  const combinedEditable = new Set(scopesSet);
                  immutableSet.forEach((k) => combinedEditable.add(k));
                  navigator?.clipboard?.writeText(
                    applyChatVisibilityLimits({
                      incomingScopes: mergeScopes(combinedEditable, unknownScopes),
                      existingScopes: initialKnown,
                      isCreate,
                      actorScopes: userScopeSet,
                      isMaster: isMasterUser,
                    }).join(",")
                  );
                }}
              >
                <ContentCopyRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2, alignItems: "center" }}>
            <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
              Atalhos:
            </Typography>
            <Tooltip title="Deixa só a permissão Ver, onde você puder alterar">
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  const next = new Set(scopesSet);
                  for (const k of Array.from(next)) {
                    if (allowedSet.has(k) && !isChatVisibilityLimitScope(k)) next.delete(k);
                  }
                  catalog.forEach((mod) =>
                    mod.resources.forEach((r) => {
                      const acts = Array.isArray(r.actions) && r.actions.length ? r.actions : ACTIONS;
                      if (acts.includes("view")) {
                        const key = `${r.key}.view`;
                        if (allowedSet.has(key)) next.add(key);
                      }
                    })
                  );
                  setScopesSet(next);
                }}
              >
                Só ver
              </Button>
            </Tooltip>

            <Tooltip title="Ver + Criar + Editar (sem exclusão nem restrições)">
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  const next = new Set(scopesSet);
                  for (const k of Array.from(next)) {
                    if (allowedSet.has(k) && !isChatVisibilityLimitScope(k)) next.delete(k);
                  }
                  catalog.forEach((mod) =>
                    mod.resources.forEach((r) => {
                      const acts = Array.isArray(r.actions) && r.actions.length ? r.actions : ACTIONS;
                      ["view", "create", "update"].forEach((a) => {
                        if (acts.includes(a)) {
                          const key = `${r.key}.${a}`;
                          if (allowedSet.has(key)) next.add(key);
                        }
                      });
                    })
                  );
                  setScopesSet(next);
                }}
              >
                Editor
              </Button>
            </Tooltip>

            <Tooltip title="Libera o máximo (sem marcar bloqueios de visão), só onde você tem acesso">
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  const next = new Set(scopesSet);
                  for (const k of Array.from(next)) {
                    if (allowedSet.has(k) && !isChatVisibilityLimitScope(k)) next.delete(k);
                  }
                  catalog.forEach((mod) =>
                    mod.resources.forEach((r) => {
                      const acts = Array.isArray(r.actions) && r.actions.length ? r.actions : ACTIONS;
                      acts.forEach((a) => {
                        if (isChatVisibilityLimitAction(a)) return;
                        const key = `${r.key}.${a}`;
                        if (allowedSet.has(key)) next.add(key);
                      });
                    })
                  );
                  setScopesSet(next);
                }}
              >
                Acesso amplo
              </Button>
            </Tooltip>

            <Tooltip title="Remove o que você pode alterar (mantém o que está travado)">
              <Button
                size="small"
                onClick={() => {
                  const next = new Set(scopesSet);
                  for (const k of Array.from(next)) {
                    if (allowedSet.has(k) && !isChatVisibilityLimitScope(k)) next.delete(k);
                  }
                  setScopesSet(next);
                }}
              >
                Limpar
              </Button>
            </Tooltip>
          </Box>

          <PermissionMatrix
            catalog={catalog}
            scopesSet={scopesSet}
            onToggle={toggleScope}
            allowedSet={allowedSet}
            immutableSet={immutableSet}
            allPositions={allPositions}
            assignablePositionIds={assignablePositionIds}
            onAssignablePositionIdsChange={setAssignablePositionIds}
          />

          <ChatVisibilityLimitsCard
            scopesSet={scopesSet}
            allowedSet={allowedSet}
            immutableSet={immutableSet}
            onToggle={toggleScope}
          />

          <Box
            sx={{
              mt: 2,
              p: 1.5,
              borderRadius: 1,
              border: "1px solid",
              borderColor: "warning.light",
              bgcolor: RESTRICT_BG,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                letterSpacing: 0.3,
                textTransform: "uppercase",
                color: "warning.dark",
                display: "block",
                mb: 0.25,
              }}
            >
              Bloquear envio por canais
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1, lineHeight: 1.35 }}>
              O padrão é enviar por todos os canais criados. Marque apenas os canais que este cargo não pode usar
              no atendimento (por exemplo, e-mail).
            </Typography>
            <BlockedChannelsPicker
              channels={outboundChannels}
              value={blockedOutboundChannelIds}
              onChange={setBlockedOutboundChannelIds}
            />
          </Box>
        </Box>

        <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.6, display: "block", mb: 1 }}>
          Mais opções
        </Typography>

        <OptionalSection
          title="Descrição"
          summary={
            String(form.descricao || "").trim()
              ? `${String(form.descricao).trim().slice(0, 80)}${String(form.descricao).trim().length > 80 ? "…" : ""}`
              : "Texto livre sobre as responsabilidades do cargo"
          }
          open={showDescription}
          onToggle={() => setShowDescription((v) => !v)}
          badge={String(form.descricao || "").trim() ? "Preenchido" : undefined}
        >
          <TextField
            name="descricao"
            value={form.descricao}
            onChange={handleChange}
            fullWidth
            multiline
            minRows={2}
            placeholder="Ex.: Atende clientes no chat e registra solicitações."
          />
        </OptionalSection>

        <OptionalSection
          title="Custo e jornada"
          summary={hrSummaryLabel(hr)}
          open={showHr}
          onToggle={() => setShowHr((v) => !v)}
          badge={positionHasHrData(hr) ? "Configurado" : undefined}
        >
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Usado na análise financeira de atendimento. Escolha a modalidade para preencher encargos padrão.
          </Typography>
          <WorkforceHrSection
            value={hr}
            onChange={setHr}
            effectiveCost={data?.effective_cost}
            positionId={form.id ? Number(form.id) : null}
            showBenchmark={Boolean(form.id)}
            hideHeader
          />
        </OptionalSection>

        <OptionalSection
          title="Roteamento da recepcionista (IA)"
          summary={
            positionHasRoutingHints(form)
              ? "Hints configurados para encaminhamento"
              : "Ajuda a IA a enviar o cliente para o perfil certo"
          }
          open={showRouting}
          onToggle={() => setShowRouting((v) => !v)}
          badge={positionHasRoutingHints(form) ? "Configurado" : undefined}
        >
          <Alert severity="info" sx={{ mb: 1.5 }}>
            Opcional. Orienta a recepcionista virtual ao encaminhar o cliente para um atendente deste cargo.
          </Alert>
          <TextField
            label="Sinónimos de cargo do cliente"
            name="routingAliases"
            value={form.routingAliases ?? ""}
            onChange={handleChange}
            fullWidth
            margin="dense"
            multiline
            minRows={2}
            placeholder="gerente, gestor comercial, coordenador de loja"
            helperText="Termos que o cliente pode usar; separe por vírgula."
          />
          <TextField
            label="Necessidades / temas atendidos"
            name="routingNeeds"
            value={form.routingNeeds ?? ""}
            onChange={handleChange}
            fullWidth
            margin="dense"
            multiline
            minRows={2}
            placeholder="financeiro, faturamento, troca de nota fiscal"
            helperText="Palavras-chave do pedido que combinam com este perfil."
          />
          <TextField
            label="Notas internas para a IA"
            name="routingNotes"
            value={form.routingNotes ?? ""}
            onChange={handleChange}
            fullWidth
            margin="dense"
            multiline
            minRows={2}
            placeholder="Ex.: priorizar quando o cliente mencionar contrato corporativo."
            helperText="Não é exibido ao cliente final."
          />
        </OptionalSection>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3 }}>
          <Button variant="outlined" onClick={onClose} disabled={isLoading}>
            Cancelar
          </Button>
          <Button type="submit" variant="contained" color="primary" disabled={isLoading}>
            {isLoading ? <CircularProgress size={22} /> : form?.id ? "Salvar" : "Criar cargo"}
          </Button>
        </Box>
      </form>
    </Paper>
  );
};

export default AddPositionForm;
